import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { MapPin, UserCheck, DollarSign, Clock, Send, CheckCircle, Upload, FileText, X, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { usePageMeta } from '../hooks/usePageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const whyChooseUs = [
  {
    icon: MapPin,
    title: 'Nationwide Placements',
    description: 'Explore career opportunities from coast to coast with our vast network of trusted employers. Whether you want to stay local or take your skills on the road, we\'ll connect you to the right role in the right place.',
  },
  {
    icon: UserCheck,
    title: 'Personalized Recruiter Support',
    description: 'Work with a dedicated recruiter who understands your unique strengths and goals. We\'re here to guide, advise, and champion you from your first conversation to your first day on the job and beyond.',
  },
  {
    icon: DollarSign,
    title: 'Weekly Pay + Benefits',
    description: 'Enjoy the security of consistent weekly pay along with benefits that support your health, finances, and future. We make sure you\'re rewarded and protected every step of your career journey.',
  },
  {
    icon: Clock,
    title: 'Work-Life Balance Options',
    description: 'Choose assignments that work for you, not the other way around. With flexible scheduling and lifestyle-friendly roles, you can excel in your career without sacrificing personal priorities.',
  },
];

const faqItems = [
  {
    question: 'What types of healthcare staffing services do you provide?',
    answer: 'We offer a wide range of staffing solutions including Travel Staffing, Local Contracts & Per Diem, Direct Hire, Managed Services, Crisis & Rapid Response Staffing, and Telehealth Support Staffing—designed to meet both short-term and long-term needs.',
  },
  {
    question: 'How quickly can you provide staff in urgent situations?',
    answer: 'Through our Crisis & Rapid Response service, we can deploy qualified professionals within hours, ensuring your facility remains fully supported during emergencies or sudden demand surges.',
  },
  {
    question: 'Do you work with both clinical and non-clinical professionals?',
    answer: 'Yes. We specialize in connecting highly qualified nursing, allied health, and non-clinical professionals with healthcare facilities nationwide.',
  },
  {
    question: 'What values guide your staffing process?',
    answer: 'Our work is built on four core values: Integrity, Compassion, Excellence, and Collaboration—ensuring ethical practices, empathy in service, high standards, and strong partnerships.',
  },
  {
    question: 'Can you support virtual care and telehealth programs?',
    answer: 'Absolutely. Our Telehealth Support Staffing service provides both clinical and administrative staff to help organizations build and scale their virtual care operations.',
  },
];

const professionOptions = [
  'Nursing',
  'Allied',
  'Physician & Advanced Practice',
  'Interim/Executive Search Academics',
  'Schools',
  'Language Interpreters',
  'Other',
];

export const ApplyNowPage = () => {
  usePageMeta({
    title: 'Apply Now - Start Your Healthcare Career',
    description: 'Apply for healthcare positions including nursing, allied health, and non-clinical roles. Upload your resume and connect with Digitive Health recruiters today.',
    path: '/apply-now',
  });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    title: '',
    phoneNumber: '',
    email: '',
    companyName: '',
    zipCode: '',
    profession: '',
    comments: '',
    acceptTerms: false,
  });
  const [resume, setResume] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please upload a PDF or Word document (.pdf, .doc, .docx)');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setResume(file);
    }
  };

  const handleRemoveResume = () => {
    setResume(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', `${formData.firstName} ${formData.lastName}`);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phoneNumber);
      formDataToSend.append('profession', formData.profession);
      formDataToSend.append('experience', `${formData.title} at ${formData.companyName}`);
      formDataToSend.append('message', `Location: ${formData.zipCode}\n${formData.comments || ''}`);
      
      if (resume) {
        formDataToSend.append('resume', resume);
      }

      const response = await fetch(`${API_URL}/api/apply`, {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            firstName: '',
            lastName: '',
            title: '',
            phoneNumber: '',
            email: '',
            companyName: '',
            zipCode: '',
            profession: '',
            comments: '',
            acceptTerms: false,
          });
          setResume(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }, 5000);
      } else {
        setSubmitError(data.detail || 'Failed to submit application. Please try again.');
      }
    } catch (error) {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="apply-now-page">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-secondary relative overflow-hidden">
        <div className="container-width">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-left">
              <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary/60 mb-4">
                Join Our Team
              </span>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
                Apply Now!
              </h1>
              <p className="text-lg text-muted-foreground">
                Your dream job could be closer than you think — and we're here to help you find it. 
                Whether you're an experienced professional or just starting your healthcare career, 
                our team is ready to connect you with the right opportunities.
              </p>
            </div>
            
            {/* Right Image */}
            <div className="relative hidden lg:block">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=400&fit=crop"
                  alt="Healthcare professional at work"
                  className="w-full h-[350px] object-cover"
                  data-testid="apply-hero-image"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-primary/20 rounded-full blur-xl" />
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-primary/10 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="section-padding" data-testid="application-form-section">
        <div className="container-width">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary/60 mb-4">
                Quick Connect
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                Fill It In, We'll Take It From Here
              </h2>
            </div>

            <Card className="p-8 md:p-10 border-border/50 shadow-lg" data-testid="application-form-card">
              {isSubmitted ? (
                <div className="text-center py-12" data-testid="form-success">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground mb-2">
                    Thank you! Your submission has been received!
                  </h3>
                  <p className="text-muted-foreground">
                    We'll get back to you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="application-form">
                  {/* First Name & Last Name */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-foreground mb-2">
                        First Name<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                        placeholder="First name"
                        data-testid="first-name-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-foreground mb-2">
                        Last Name<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                        placeholder="Last name"
                        data-testid="last-name-input"
                      />
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-foreground mb-2">
                      Title<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                      placeholder="Your job title"
                      data-testid="title-input"
                    />
                  </div>

                  {/* Phone Number & Email */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phoneNumber" className="block text-sm font-medium text-foreground mb-2">
                        Phone Number<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                        placeholder="(123) 456-7890"
                        data-testid="phone-number-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                        Email Address<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                        placeholder="email@example.com"
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  {/* Company Name & Zip Code */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-foreground mb-2">
                        Company Name<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                        placeholder="Company name"
                        data-testid="company-name-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="zipCode" className="block text-sm font-medium text-foreground mb-2">
                        Zip Code<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                        placeholder="12345"
                        data-testid="zip-code-input"
                      />
                    </div>
                  </div>

                  {/* Profession */}
                  <div>
                    <label htmlFor="profession" className="block text-sm font-medium text-foreground mb-2">
                      Profession<span className="text-red-500">*</span>
                    </label>
                    <select
                      id="profession"
                      name="profession"
                      value={formData.profession}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                      data-testid="profession-select"
                    >
                      <option value="">Select your profession</option>
                      {professionOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Resume Upload */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Resume/CV
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="hidden"
                        id="resume-upload"
                        data-testid="resume-input"
                      />
                      
                      {!resume ? (
                        <label
                          htmlFor="resume-upload"
                          className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                          data-testid="resume-dropzone"
                        >
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                            <p className="mb-1 text-sm text-muted-foreground">
                              <span className="font-semibold text-primary">Click to upload</span> your resume
                            </p>
                            <p className="text-xs text-muted-foreground">
                              PDF, DOC, or DOCX (Max 5MB)
                            </p>
                          </div>
                        </label>
                      ) : (
                        <div 
                          className="flex items-center justify-between w-full p-4 border border-border rounded-xl bg-green-50"
                          data-testid="resume-preview"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <FileText className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {resume.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {(resume.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemoveResume}
                            className="p-2 hover:bg-red-100 rounded-full transition-colors"
                            data-testid="remove-resume-btn"
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Questions/Comments */}
                  <div>
                    <label htmlFor="comments" className="block text-sm font-medium text-foreground mb-2">
                      Questions/Comments
                    </label>
                    <textarea
                      id="comments"
                      name="comments"
                      value={formData.comments}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none"
                      placeholder="Any additional information or questions..."
                      data-testid="comments-textarea"
                    />
                  </div>

                  {/* Terms Checkbox */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleChange}
                      required
                      className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      data-testid="accept-terms-checkbox"
                    />
                    <label htmlFor="acceptTerms" className="text-sm text-muted-foreground">
                      I accept Cookies & Privacy Policies and agree to receive SMS/Email Communication. 
                      By providing a telephone number and submitting this form you are consenting to be 
                      contacted by SMS text message. Message & data rates may apply. You can reply STOP 
                      to opt-out of further messaging.
                    </label>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary hover:bg-primary/90 text-white btn-hover-lift rounded-xl py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-application-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>

                  {submitError && (
                    <div className="mt-4 p-4 rounded-xl text-center bg-red-100 text-red-800 border border-red-200">
                      {submitError}
                    </div>
                  )}
                </form>
              )}
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-secondary" data-testid="why-choose-section">
        <div className="container-width">
          <div className="text-center mb-12">
            <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary/60 mb-4">
              Why Choose Us?
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground max-w-3xl mx-auto">
              When it comes to the right opportunity, it's about more than a job title
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              It's about matching your skills, goals, and lifestyle with the perfect fit. We connect you to 
              top employers nationwide, provide expert guidance, and offer the pay, benefits, and flexibility 
              you need to thrive.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {whyChooseUs.map((item, index) => (
              <Card
                key={index}
                className="p-6 border-border/50 card-hover"
                data-testid={`why-choose-card-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding" data-testid="faq-section">
        <div className="container-width">
          <div className="text-center mb-12">
            <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary/60 mb-4">
              Frequently Asked Questions
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              Your Questions, Answered
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full" data-testid="apply-faq-accordion">
              {faqItems.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border-b border-border/50"
                  data-testid={`apply-faq-item-${index}`}
                >
                  <AccordionTrigger className="faq-trigger text-left font-semibold text-foreground hover:text-primary">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-muted-foreground leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ApplyNowPage;
